import React from "react";
import { Box, Typography, Card, CardContent, CardMedia, Button, Container } from "@mui/material"
import Grid from "@mui/material/Grid2"
import UbehaviorPicture from "../img/Ubehavior-project.png"
import DawnPicture from "../img/Dawn-project.png"
import PersonalWebsitePhoto from "../img/PersonalWebsite-project.png"
import CSUHackathonPhoto from "../img/CSU-Hackathon-project.png"
import KubernetesCloudProject from "../img/Kubernetes-Cloud-Project.png"

const projects = [
    {
        title: "Kubernetes Cloud Challenge",
        description: "Deployed a full-stack web application on AWS EKS using Kubernetes. The project covers cluster provisioning, containerized microservices, a managed database, ConfigMaps, Secrets, and multi-service networking — demonstrating production-grade cloud-native deployment patterns.",
        image: KubernetesCloudProject,
        tags: ["Kubernetes", "AWS EKS", "Docker"],
        githubLink: "https://github.com/Quinn-p/Kubernets-Cloud-Challenge",
        liveLink: null
    },
    {
        title: "Personal Website Portfolio",
        description: "A personal portfolio website built to showcase skills and abilities learned through professional and personal experiences. Built with React and deployed via AWS.",
        image: PersonalWebsitePhoto,
        tags: ["React", "AWS", "CI/CD"],
        githubLink: "https://github.com/Quinn-p/Cloud-Resume-Challenge-Public",
        liveLink: "https://quinnp.click"
    },
    {
        title: "UBehavior",
        description: "A learning tool integrated into Canvas that shows students how well they are practicing spacing and mixing methods of learning to keep them on track with repetitive practice.",
        image: UbehaviorPicture,
        tags: ["EdTech", "Canvas LMS", "Python"],
        githubLink: null,
        liveLink: "https://www.chhs.colostate.edu/alt/research-projects/u-behavior/"
    },
    {
        title: "DAWN Dashboard",
        description: "An agricultural dashboard to help farmers with tools and information that can keep them updated with what they need.",
        image: DawnPicture,
        tags: ["Docker", "MongoDB", "Python"],
        githubLink: null,
        liveLink: "https://dawn.umd.edu"
    },
    {
        title: "CSU Hackathon",
        description: "Developed a solution using Bidirectional A* algorithm to navigate a robot across unknown terrain. The implementation focused on efficient pathfinding while considering dynamic obstacles and terrain variations.",
        image: CSUHackathonPhoto,
        tags: ["Algorithms", "Pathfinding", "Python"],
        githubLink: "https://github.com/Quinn-p/CSU-Hackathon-Rover",
        liveLink: null
    }
];

const Projects = () => {
    return (
        <div id="projects">
            <Box sx={{ bgcolor: '#f8faff' }}>
                <Container maxWidth="lg" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography variant="overline" sx={{ color: '#007FFF', letterSpacing: '0.15em', fontSize: '0.85rem' }}>
                            What I've Built
                        </Typography>
                        <Typography variant="h3" fontWeight={700} mt={1}>
                            Projects
                        </Typography>
                        <Typography variant="body1" color="text.secondary" mt={1}>
                            A selection of projects built personally, through internships, and academic work.
                        </Typography>
                    </Box>

                    <Grid container spacing={3}>
                        {projects.map((project, index) => (
                            <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={index}>
                                <Card sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-6px)',
                                        boxShadow: '0 16px 40px rgba(0,0,0,0.12)',
                                        '& .project-image': { transform: 'scale(1.05)' }
                                    }
                                }}>
                                    <Box sx={{ overflow: 'hidden', height: 200 }}>
                                        <CardMedia
                                            component="img"
                                            image={project.image}
                                            alt={project.title}
                                            className="project-image"
                                            sx={{
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.5s ease'
                                            }}
                                        />
                                    </Box>
                                    <CardContent sx={{
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        p: 3
                                    }}>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                                            {project.tags.map((tag) => (
                                                <Box key={tag} sx={{
                                                    bgcolor: 'rgba(0,127,255,0.08)',
                                                    color: '#007FFF',
                                                    border: '1px solid rgba(0,127,255,0.2)',
                                                    borderRadius: '6px',
                                                    px: 1, py: 0.25,
                                                    fontSize: '0.7rem',
                                                    fontWeight: 600
                                                }}>
                                                    {tag}
                                                </Box>
                                            ))}
                                        </Box>
                                        <Typography variant="h6" fontWeight={700} gutterBottom>
                                            {project.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, flexGrow: 1 }}>
                                            {project.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
                                            {project.githubLink && (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    href={project.githubLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        borderColor: '#007FFF',
                                                        color: '#007FFF',
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        '&:hover': { bgcolor: 'rgba(0,127,255,0.06)' }
                                                    }}
                                                >
                                                    GitHub
                                                </Button>
                                            )}
                                            {project.liveLink && (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    href={project.liveLink}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        bgcolor: '#007FFF',
                                                        borderRadius: '8px',
                                                        textTransform: 'none',
                                                        fontWeight: 600,
                                                        '&:hover': { bgcolor: '#0060CC' }
                                                    }}
                                                >
                                                    Live Site
                                                </Button>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>
        </div>
    );
}

export default Projects;
